import { useMemo, useState } from "react";
import { useStore, uid } from "../store/store";
import {
  evaluateSend,
  handleInbound,
  type ContactSnapshot,
  type CampaignSnapshot,
} from "@mensajeando/compliance";
import { step, type ConversationState } from "@mensajeando/flows";
import type { Conversation, Language } from "../store/types";

// Operator-facing console. Single language (English) by design — phonebank
// agents work in one language while the contact-side messaging is bilingual.
const SLA_WARN_MIN = 5;
const SLA_BREACH_MIN = 10;

function minutesSince(iso?: string): number | null {
  if (!iso) return null;
  return Math.round((Date.now() - new Date(iso).getTime()) / 60000);
}

function maskPhone(p: string): string {
  // Show only last 4 digits — PII guard in the operator UI
  return `••• ••• ${p.slice(-4)}`;
}

function slaTone(state: Conversation["state"], lastInbound?: string): "ok" | "warn" | "breach" | "idle" {
  if (state !== "awaiting_agent") return "idle";
  const m = minutesSince(lastInbound);
  if (m == null) return "idle";
  if (m >= SLA_BREACH_MIN) return "breach";
  if (m >= SLA_WARN_MIN) return "warn";
  return "ok";
}

export function AgentConsole() {
  const conversations = useStore((s) => s.conversations);
  const contacts = useStore((s) => s.contacts);
  const campaigns = useStore((s) => s.campaigns);
  const consents = useStore((s) => s.consents);
  const flows = useStore((s) => s.flows);
  const events = useStore((s) => s.events);
  const agents = useStore((s) => s.agents);
  const orgs = useStore((s) => s.organizations);
  const clients = useStore((s) => s.clients);

  const me = agents.find((a) => a.id === "agent_me")!;
  const updateAgent = useStore((s) => s.updateAgent);
  const updateConversation = useStore((s) => s.updateConversation);
  const updateContact = useStore((s) => s.updateContact);
  const addConsent = useStore((s) => s.addConsent);
  const appendEvent = useStore((s) => s.appendEvent);

  // Build the agent's queue: assigned-to-me first, then unassigned awaiting_agent
  const queue = useMemo(() => {
    const mine = conversations.filter((c) => c.assignedAgentId === me.id && c.state !== "completed");
    const open = conversations.filter(
      (c) => !c.assignedAgentId && c.state === "awaiting_agent",
    );
    return [...mine, ...open].sort((a, b) => {
      // Awaiting agent first, oldest inbound on top
      const sa = a.state === "awaiting_agent" ? 0 : 1;
      const sb = b.state === "awaiting_agent" ? 0 : 1;
      if (sa !== sb) return sa - sb;
      const at = new Date(a.lastInboundAt ?? a.startedAt).getTime();
      const bt = new Date(b.lastInboundAt ?? b.startedAt).getTime();
      return at - bt;
    });
  }, [conversations, me.id]);

  const [activeId, setActiveId] = useState<string | undefined>(queue[0]?.id);
  const active = conversations.find((c) => c.id === activeId);
  const contact = contacts.find((c) => c.id === active?.contactId);
  const campaign = campaigns.find((c) => c.id === active?.campaignId);
  const org = orgs.find((o) => o.id === campaign?.organizationId);
  const client = clients.find((c) => c.id === campaign?.clientId);

  // Thread for the active conversation
  const thread = useMemo(() => {
    if (!active) return [];
    return events
      .filter((e) => e.contactId === active.contactId && e.campaignId === active.campaignId)
      .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  }, [events, active]);

  // Compliance check for the active conversation
  const policy = useMemo(() => {
    if (!campaign || !contact) return undefined;
    const contactConsents = consents.filter((x) => x.contactId === contact.id);
    const contactSnap: ContactSnapshot = {
      id: contact.id,
      phoneE164: contact.phoneE164,
      language: contact.language,
      timezone: contact.timezone,
      isLandline: contact.isLandline,
      isReassignedRisk: contact.isReassignedRisk,
      consentBySource: Object.fromEntries(
        contactConsents.map((c) => [c.id, {
          channel: c.channel, status: c.status, at: c.at, disclosureVersion: c.disclosureVersion,
        }]),
      ),
      suppressed: contact.suppressed,
      suppressionReason: contact.suppressionReason,
    };
    const campSnap: CampaignSnapshot = {
      id: campaign.id,
      channel: campaign.channel,
      senderEligible: campaign.senderEligible,
      campaignRegistered: campaign.campaignRegistered,
      requiresConsent: campaign.requiresConsent,
      organizationId: campaign.organizationId,
    };
    return evaluateSend({ contact: contactSnap, campaign: campSnap });
  }, [campaign, contact, consents]);

  // Suggested next message from the flow
  const suggested = useMemo(() => {
    if (!campaign || !contact || !active) return undefined;
    const flow = flows[campaign.flowId];
    if (!flow) return undefined;
    const language: Language = campaign.language === "auto" ? contact.language : campaign.language;
    const state: ConversationState = {
      flowId: flow.id,
      contactId: contact.id,
      language,
      currentNode: active.currentFlowNodeId ?? flow.entry,
      answers: {},
      tags: new Set(contact.tags),
    };
    const out = step(flow, state);
    return { body: out.outboundBody, nodeId: out.state.currentNode, language };
  }, [campaign, contact, active, flows]);

  function takeAndSend() {
    if (!active || !contact || !campaign || !policy?.ok || !suggested?.body) return;
    // Assign to me if unassigned
    if (active.assignedAgentId !== me.id) {
      updateConversation(active.id, { assignedAgentId: me.id });
    }
    const now = new Date().toISOString();
    appendEvent({
      id: uid("ev"),
      campaignId: campaign.id,
      contactId: contact.id,
      channel: campaign.channel,
      language: suggested.language,
      direction: "outbound",
      status: "delivered",
      body: suggested.body,
      flowNodeId: suggested.nodeId,
      at: now,
    });
    updateConversation(active.id, {
      state: "awaiting_voter",
      lastOutboundAt: now,
      currentFlowNodeId: suggested.nodeId,
    });
    updateAgent(me.id, { handledToday: me.handledToday + 1 });
  }

  function pressStop(reason: string) {
    if (!active || !contact || !campaign || !org || !client) return;
    const handled = handleInbound({
      body: contact.language === "es" ? "PARE" : "STOP",
      channel: campaign.channel,
      fromE164: contact.phoneE164,
      toE164: "+15555550100",
      receivedAt: new Date().toISOString(),
      campaignId: campaign.id,
      organizationId: campaign.organizationId,
      contactLanguage: contact.language,
      campaignType: campaign.type,
      clientName: client.name,
      supportEmail: org.supportEmail,
    });
    updateContact(contact.id, { suppressed: true, suppressionReason: `Agent STOP — ${reason}` });
    addConsent({
      id: uid("consent"),
      contactId: contact.id,
      channel: campaign.channel,
      language: contact.language,
      status: "revoked",
      source: "verbal_logged",
      disclosureVersion: "2026-01-15.v1",
      at: handled.auditEvent.at,
    });
    appendEvent({
      id: uid("ev"),
      campaignId: campaign.id,
      contactId: contact.id,
      channel: campaign.channel,
      language: contact.language,
      direction: "inbound",
      body: `[Agent-logged STOP] ${reason}`,
      at: new Date().toISOString(),
      optOut: true,
    });
    updateConversation(active.id, { state: "opted_out" });
    updateAgent(me.id, { optOutsToday: me.optOutsToday + 1 });
  }

  function pressEscalate() {
    if (!active) return;
    updateConversation(active.id, { state: "escalated", flagged: true, escalationReason: "Agent flagged for supervisor" });
    updateAgent(me.id, { escalations: me.escalations + 1 });
  }

  return (
    <div className="-m-8 grid h-[calc(100vh-0px)] grid-rows-[auto_1fr]">
      {/* Top bar — agent state */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="text-sm">
            <div className="text-xs uppercase tracking-wider text-slate-500">Agent</div>
            <div className="font-semibold">{me.name}</div>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div className="flex items-center gap-1">
            {(["available", "wrap_up", "break", "off"] as const).map((s) => (
              <button
                key={s}
                onClick={() => updateAgent(me.id, { state: s })}
                className={`rounded px-2.5 py-1 text-xs font-medium ${
                  me.state === s
                    ? s === "available"
                      ? "bg-brand-600 text-white"
                      : s === "wrap_up"
                      ? "bg-accent-400 text-ink-900"
                      : s === "break"
                      ? "bg-slate-700 text-white"
                      : "bg-slate-200 text-slate-700"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <Stat label="Handled" value={me.handledToday} />
          <Stat label="Opt-outs" value={me.optOutsToday} />
          <Stat label="Avg reply" value={`${me.avgResponseSec}s`} />
          <Stat label="Escalated" value={me.escalations} />
        </div>
      </div>

      {/* Three-column workspace */}
      <div className="grid min-h-0 grid-cols-[300px_1fr_320px]">
        {/* LEFT — queue */}
        <aside className="overflow-y-auto border-r border-slate-200 bg-slate-50">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-slate-50/95 px-4 py-2 backdrop-blur">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Your queue</h2>
            <span className="rounded-full bg-ink-900 px-2 py-0.5 text-[10px] font-semibold text-white">{queue.length}</span>
          </div>
          <ul className="divide-y divide-slate-200">
            {queue.map((c) => {
              const ct = contacts.find((x) => x.id === c.contactId);
              const last = events
                .filter((e) => e.contactId === c.contactId && e.campaignId === c.campaignId)
                .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
              const mins = minutesSince(c.lastInboundAt);
              const tone = slaTone(c.state, c.lastInboundAt);
              return (
                <li key={c.id}>
                  <button
                    onClick={() => setActiveId(c.id)}
                    className={`flex w-full items-start gap-2 px-4 py-3 text-left transition ${
                      activeId === c.id ? "bg-white shadow-inner" : "hover:bg-white"
                    }`}
                  >
                    <span
                      className={`mt-1.5 inline-block h-2.5 w-2.5 rounded-full ${
                        tone === "breach"
                          ? "bg-red-500 animate-pulse"
                          : tone === "warn"
                          ? "bg-accent-400"
                          : c.state === "awaiting_agent"
                          ? "bg-brand-500"
                          : c.state === "escalated"
                          ? "bg-red-500"
                          : "bg-slate-300"
                      }`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-ink-900">
                          {ct?.firstName ?? "—"}
                          <span className="ml-1 rounded bg-slate-200 px-1 text-[9px] font-bold uppercase text-slate-600">
                            {c.language}
                          </span>
                        </span>
                        {c.state === "awaiting_agent" && mins != null && (
                          <span
                            className={`text-[10px] font-semibold tabular-nums ${
                              tone === "breach" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "text-slate-500"
                            }`}
                          >
                            {mins}m
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-slate-500">
                        {last?.body ?? "—"}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
            {queue.length === 0 && (
              <li className="px-4 py-8 text-center text-xs text-slate-400">Queue empty.</li>
            )}
          </ul>
        </aside>

        {/* CENTER — thread */}
        <section className="flex min-h-0 flex-col bg-white">
          {!active || !contact ? (
            <div className="flex flex-1 items-center justify-center text-sm text-slate-400">
              Pick a conversation from the queue.
            </div>
          ) : (
            <>
              {/* Compliance ribbon */}
              <div
                className={`flex items-center justify-between border-b px-6 py-2 text-xs font-medium ${
                  policy?.ok ? "border-brand-200 bg-brand-50 text-brand-800" : "border-red-200 bg-red-50 text-red-800"
                }`}
              >
                <span className="flex items-center gap-3">
                  <span>{policy?.ok ? "✓ Cleared to send" : "✖ Blocked"}</span>
                  <span className="opacity-70">·</span>
                  <span>Lang lock: {contact.language.toUpperCase()}</span>
                  <span className="opacity-70">·</span>
                  <span>Quiet hours: OK</span>
                  <span className="opacity-70">·</span>
                  <span>{contact.suppressed ? "Suppressed" : "Active"}</span>
                </span>
                <span className="opacity-70">
                  {policy?.ok ? "Policy: pass" : `Code: ${policy?.code} · ${policy?.reason}`}
                </span>
              </div>

              {/* Messages */}
              <div className="flex-1 space-y-3 overflow-y-auto bg-gradient-to-b from-slate-50 to-white px-6 py-4">
                {thread.length === 0 && (
                  <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">
                    No messages yet. Send the suggested greeting below.
                  </div>
                )}
                {thread.map((e) => (
                  <div
                    key={e.id}
                    className={`flex ${e.direction === "outbound" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[68%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                        e.direction === "outbound"
                          ? "bg-ink-900 text-white"
                          : "border border-slate-200 bg-white text-ink-900"
                      }`}
                    >
                      <div>{e.body}</div>
                      <div
                        className={`mt-1 text-[10px] uppercase tracking-wider ${
                          e.direction === "outbound" ? "text-white/60" : "text-slate-400"
                        }`}
                      >
                        {new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {e.direction === "outbound" && e.status ? ` · ${e.status}` : ""}
                        {e.optOut ? " · OPT-OUT" : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Composer */}
              <div className="border-t border-slate-200 bg-white px-6 py-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Suggested next ({suggested?.language?.toUpperCase()})
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={pressEscalate} className="rounded border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50">
                      ⚑ Escalate
                    </button>
                    <button
                      onClick={() => pressStop("Voter requested STOP verbally")}
                      className="rounded bg-red-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-red-700"
                    >
                      STOP
                    </button>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-ink-900">
                  {suggested?.body ?? "Flow has no next step."}
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="flex gap-1 text-xs">
                    <button className="rounded border border-slate-300 bg-white px-2 py-1 hover:bg-slate-50">1 — Yes / Sí</button>
                    <button className="rounded border border-slate-300 bg-white px-2 py-1 hover:bg-slate-50">2 — No</button>
                    <button className="rounded border border-slate-300 bg-white px-2 py-1 hover:bg-slate-50">3 — Repeat</button>
                  </div>
                  <button
                    onClick={takeAndSend}
                    disabled={!policy?.ok || !suggested?.body}
                    className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Send →
                  </button>
                </div>
                <div className="mt-2 text-[10px] text-slate-400">
                  Free-text disabled for this campaign type. All sends route through the policy engine and are audited.
                </div>
              </div>
            </>
          )}
        </section>

        {/* RIGHT — voter card */}
        <aside className="overflow-y-auto border-l border-slate-200 bg-white">
          {!contact ? (
            <div className="p-6 text-xs text-slate-400">No contact selected.</div>
          ) : (
            <div className="space-y-4 p-5">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Voter</div>
                <div className="text-lg font-semibold">{contact.firstName}</div>
                <div className="font-mono text-xs text-slate-500">{maskPhone(contact.phoneE164)}</div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
                <Row k="Language" v={contact.language.toUpperCase()} />
                <Row k="Timezone" v={contact.timezone ?? "—"} />
                <Row k="Tags" v={contact.tags.length ? contact.tags.join(", ") : "—"} />
                <Row k="Status" v={contact.suppressed ? "Suppressed" : "Active"} />
              </div>

              <div>
                <div className="mb-2 text-[10px] uppercase tracking-wider text-slate-500">Consent history</div>
                <ul className="space-y-1.5 text-xs">
                  {consents
                    .filter((c) => c.contactId === contact.id)
                    .map((c) => (
                      <li key={c.id} className="rounded border border-slate-200 bg-white px-2 py-1.5">
                        <div className="flex items-center justify-between">
                          <span className={`font-semibold ${c.status === "granted" ? "text-brand-700" : "text-red-700"}`}>
                            {c.status}
                          </span>
                          <span className="text-[10px] text-slate-400">{c.source}</span>
                        </div>
                        <div className="text-[10px] text-slate-500">{new Date(c.at).toLocaleString()}</div>
                        <div className="text-[10px] text-slate-400">v{c.disclosureVersion}</div>
                      </li>
                    ))}
                  {consents.filter((c) => c.contactId === contact.id).length === 0 && (
                    <li className="text-[11px] text-slate-400">No consent on file.</li>
                  )}
                </ul>
              </div>

              <div className="border-t border-slate-200 pt-3">
                <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Quick STOP reason</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {["Requested verbally", "Wrong number", "Deceased", "Language mismatch"].map((r) => (
                    <button
                      key={r}
                      onClick={() => pressStop(r)}
                      className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-700 hover:bg-red-100"
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-semibold tabular-nums text-ink-900">{value}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-200/60 py-1 last:border-0">
      <span className="text-slate-500">{k}</span>
      <span className="font-medium text-ink-900">{v}</span>
    </div>
  );
}
